'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function BigNumberCalculator() {
  const t = useTranslations("tools.bigNumberCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [numberX, setNumberX] = useState('');
  const [numberY, setNumberY] = useState('');
  const [precision, setPrecision] = useState(20);
  const [result, setResult] = useState<{ operation: string; value: string } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const parseNumber = (str: string): number => {
    const trimmed = str.trim();
    if (trimmed.match(/^-?\d+\.?\d*e[+-]?\d+$/i)) {
      return parseFloat(trimmed);
    }
    return parseFloat(trimmed);
  };

  const formatResult = (num: number, prec: number): string => {
    if (!isFinite(num)) throw new Error(t('errorInfinite'));

    if (Math.abs(num) > 1e15 || (Math.abs(num) < 1e-6 && num !== 0)) {
      return num.toExponential(prec);
    }

    const factor = Math.pow(10, prec);
    const rounded = Math.round(num * factor) / factor;
    return rounded.toString();
  };

  const handleOperation = (operation: string) => {
    setError('');
    setResult(null);

    try {
      if (numberX.trim() === '') {
        throw new Error(t('errorEnterX'));
      }

      const x = parseNumber(numberX);
      if (isNaN(x)) {
        throw new Error(t('errorInvalidX'));
      }

      let resultValue: string;
      let operationLabel: string;

      switch(operation) {
        case 'add': {
          if (numberY.trim() === '') throw new Error(t('errorEnterY'));
          const y = parseNumber(numberY);
          if (isNaN(y)) throw new Error(t('errorInvalidY'));
          resultValue = formatResult(x + y, precision);
          operationLabel = `${numberX} + ${numberY} =`;
          break;
        }
        case 'subtract': {
          if (numberY.trim() === '') throw new Error(t('errorEnterY'));
          const y = parseNumber(numberY);
          if (isNaN(y)) throw new Error(t('errorInvalidY'));
          resultValue = formatResult(x - y, precision);
          operationLabel = `${numberX} − ${numberY} =`;
          break;
        }
        case 'multiply': {
          if (numberY.trim() === '') throw new Error(t('errorEnterY'));
          const y = parseNumber(numberY);
          if (isNaN(y)) throw new Error(t('errorInvalidY'));
          resultValue = formatResult(x * y, precision);
          operationLabel = `${numberX} × ${numberY} =`;
          break;
        }
        case 'divide': {
          if (numberY.trim() === '') throw new Error(t('errorEnterY'));
          const y = parseNumber(numberY);
          if (isNaN(y)) throw new Error(t('errorInvalidY'));
          if (y === 0) throw new Error(t('errorDivisionByZero'));
          resultValue = formatResult(x / y, precision);
          operationLabel = `${numberX} ÷ ${numberY} =`;
          break;
        }
        case 'power': {
          if (numberY.trim() === '') throw new Error(t('errorEnterY'));
          const y = parseNumber(numberY);
          if (isNaN(y)) throw new Error(t('errorInvalidY'));
          const res = Math.pow(x, y);
          if (!isFinite(res)) throw new Error(t('errorTooLarge'));
          resultValue = formatResult(res, precision);
          operationLabel = `${numberX} ^ ${numberY} =`;
          break;
        }
        case 'sqrt': {
          if (x < 0) throw new Error(t('errorNegativeSqrt'));
          resultValue = formatResult(Math.sqrt(x), precision);
          operationLabel = `√${numberX} =`;
          break;
        }
        case 'square': {
          resultValue = formatResult(x * x, precision);
          operationLabel = `${numberX}² =`;
          break;
        }
        case 'factorial': {
          if (x < 0) throw new Error(t('errorNegativeFactorial'));
          if (x > 170) throw new Error(t('errorFactorialTooLarge'));
          if (x !== Math.floor(x)) throw new Error(t('errorFactorialInteger'));
          let fact = 1;
          for (let i = 2; i <= x; i++) {
            fact *= i;
          }
          resultValue = fact.toExponential(20);
          operationLabel = `${numberX}! =`;
          break;
        }
        case 'mod': {
          if (numberY.trim() === '') throw new Error(t('errorEnterY'));
          const y = parseNumber(numberY);
          if (isNaN(y)) throw new Error(t('errorInvalidY'));
          if (y === 0) throw new Error(t('errorModuloByZero'));
          resultValue = (x % y).toString();
          operationLabel = `${numberX} mod ${numberY} =`;
          break;
        }
        case 'gcd': {
          if (numberY.trim() === '') throw new Error(t('errorEnterY'));
          const y = parseNumber(numberY);
          if (isNaN(y)) throw new Error(t('errorInvalidY'));
          let a = Math.abs(Math.floor(x));
          let b = Math.abs(Math.floor(y));
          while (b !== 0) {
            const temp = b;
            b = a % b;
            a = temp;
          }
          resultValue = a.toString();
          operationLabel = `GCD(${numberX}, ${numberY}) =`;
          break;
        }
        case 'lcm': {
          if (numberY.trim() === '') throw new Error(t('errorEnterY'));
          const y = parseNumber(numberY);
          if (isNaN(y)) throw new Error(t('errorInvalidY'));
          const a = Math.abs(Math.floor(x));
          const b = Math.abs(Math.floor(y));
          if (a === 0 || b === 0) {
            resultValue = '0';
          } else {
            let tempA = a;
            let tempB = b;
            while (tempB !== 0) {
              const temp = tempB;
              tempB = tempA % tempB;
              tempA = temp;
            }
            const gcdValue = tempA;
            resultValue = ((a * b) / gcdValue).toString();
          }
          operationLabel = `LCM(${numberX}, ${numberY}) =`;
          break;
        }
        default:
          throw new Error(t('errorUnknownOperation'));
      }

      setResult({ operation: operationLabel, value: resultValue });
    } catch (err: any) {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    }
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    { marginBottom: '1.5rem' },
    { marginBottom: '2rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2, margin: 0 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: '1rem 0 0 0' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' },
    { padding: '1.5rem', maxWidth: '100%' },
    { padding: '2rem', maxWidth: '600px' },
    { padding: '2.5rem', maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    { marginBottom: '1.5rem' },
    { marginBottom: '2rem' },
    { marginBottom: '2rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', margin: 0 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const toolDescriptionStyle = getResponsiveStyle(
    { color: colors.textSecondary, textAlign: 'center' as const, lineHeight: 1.6, marginBottom: '1.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const infoBoxStyle = getResponsiveStyle(
    {
      background: '#eff6ff',
      borderLeft: '4px solid #3b82f6',
      borderRadius: '0.5rem',
      marginBottom: '1.5rem',
      color: '#1e40af',
      lineHeight: 1.6
    },
    { padding: '0.875rem', fontSize: '0.85rem' },
    { padding: '1rem', fontSize: '0.9rem' },
    { padding: '1rem', fontSize: '0.9rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    { display: 'flex', flexDirection: 'column' as const, gap: '0.5rem', marginBottom: '1.5rem' },
    {},
    {},
    {}
  );

  const labelStyle = getResponsiveStyle(
    { color: '#334155', fontWeight: 600 },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const textareaStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontFamily: "'Courier New', monospace",
      transition: 'all 0.3s ease',
      resize: 'vertical' as const,
      minHeight: '80px',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '0.95rem' },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '0.95rem', width: '100%' },
    { padding: '0.875rem', fontSize: '1rem', width: '150px' },
    { padding: '0.875rem', fontSize: '1rem', width: '150px' }
  );

  const precisionGroupStyle = getResponsiveStyle(
    { display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' as const },
    { flexDirection: 'column' as const, alignItems: 'flex-start' },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const precisionInfoStyle = getResponsiveStyle(
    { color: colors.textSecondary },
    { fontSize: '0.8rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.875rem' }
  );

  const operationButtonsStyle = getResponsiveStyle(
    { display: 'grid', gap: '0.75rem', marginTop: '1rem' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }
  );

  const operationBtnStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
      minHeight: '44px'
    },
    { padding: '0.75rem 0.875rem', fontSize: '0.95rem' },
    { padding: '0.875rem 1rem', fontSize: '1rem' },
    { padding: '0.875rem 1rem', fontSize: '1rem' }
  );

  const errorMessageStyle = {
    background: '#fef2f2',
    border: '2px solid #fecaca',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    display: error ? 'block' : 'none'
  };

  const resultsStyle = getResponsiveStyle(
    {
      marginTop: '2rem',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '0.75rem',
      border: '2px solid #86efac',
      display: result ? 'block' : 'none'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { color: '#166534', margin: '0 0 1.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const resultItemStyle = getResponsiveStyle(
    { background: 'white', borderRadius: '0.5rem', borderLeft: '4px solid #2563eb' },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.25rem' }
  );

  const resultLabelStyle = getResponsiveStyle(
    { color: colors.textSecondary, fontWeight: 600, display: 'block', marginBottom: '0.5rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    {
      color: colors.textPrimary,
      fontWeight: 700,
      fontFamily: "'Courier New', monospace",
      wordBreak: 'break-all' as const,
      display: 'block',
      lineHeight: 1.6
    },
    { fontSize: '1.1rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.7, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.7, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginTop: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: 500,
    transition: 'color 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t('toolTitle')}</h2>
          </div>

          <div style={toolDescriptionStyle}>
            {t('toolDescription')}
          </div>

          <div style={infoBoxStyle}>
            💡 <strong>{t('tipLabel')}</strong> {t('tipText')}
          </div>

          <div>
            <div style={formGroupStyle}>
              <label htmlFor="number-x" style={labelStyle}>{t('numberXLabel')}</label>
              <textarea
                id="number-x"
                placeholder={t('numberXPlaceholder')}
                value={numberX}
                onChange={(e) => setNumberX(e.target.value)}
                style={textareaStyle}
              />
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="number-y" style={labelStyle}>{t('numberYLabel')}</label>
              <textarea
                id="number-y"
                placeholder={t('numberYPlaceholder')}
                value={numberY}
                onChange={(e) => setNumberY(e.target.value)}
                style={textareaStyle}
              />
            </div>

            <div style={formGroupStyle}>
              <div style={precisionGroupStyle}>
                <label htmlFor="precision" style={labelStyle}>{t('precisionLabel')}</label>
                <input
                  type="number"
                  id="precision"
                  value={precision}
                  min="0"
                  max="100"
                  onChange={(e) => setPrecision(parseInt(e.target.value) || 20)}
                  style={inputStyle}
                />
                <span style={precisionInfoStyle}>{t('precisionInfo')}</span>
              </div>
            </div>

            <div style={operationButtonsStyle}>
              <button
                type="button"
                style={operationBtnStyle}
                onClick={() => handleOperation('add')}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {t('btnAdd')}
              </button>
              <button
                type="button"
                style={operationBtnStyle}
                onClick={() => handleOperation('subtract')}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {t('btnSubtract')}
              </button>
              <button
                type="button"
                style={operationBtnStyle}
                onClick={() => handleOperation('multiply')}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {t('btnMultiply')}
              </button>
              <button
                type="button"
                style={operationBtnStyle}
                onClick={() => handleOperation('divide')}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {t('btnDivide')}
              </button>
              <button
                type="button"
                style={operationBtnStyle}
                onClick={() => handleOperation('power')}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {t('btnPower')}
              </button>
              <button
                type="button"
                style={operationBtnStyle}
                onClick={() => handleOperation('sqrt')}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {t('btnSqrt')}
              </button>
              <button
                type="button"
                style={operationBtnStyle}
                onClick={() => handleOperation('square')}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {t('btnSquare')}
              </button>
              <button
                type="button"
                style={operationBtnStyle}
                onClick={() => handleOperation('factorial')}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {t('btnFactorial')}
              </button>
              <button
                type="button"
                style={operationBtnStyle}
                onClick={() => handleOperation('mod')}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {t('btnMod')}
              </button>
              <button
                type="button"
                style={operationBtnStyle}
                onClick={() => handleOperation('gcd')}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {t('btnGcd')}
              </button>
              <button
                type="button"
                style={operationBtnStyle}
                onClick={() => handleOperation('lcm')}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {t('btnLcm')}
              </button>
            </div>
          </div>

          <div style={errorMessageStyle}>
            {error}
          </div>

          <div style={resultsStyle}>
            <h3 style={resultsH3Style}>✨ {t('resultTitle')}</h3>
            <div style={resultItemStyle}>
              <span style={resultLabelStyle}>{result?.operation}</span>
              <span style={resultValueStyle}>{result?.value}</span>
            </div>
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>

        <h3 style={h3Style}>{t('bestPracticesTitle')}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('accuracyTitle')}</strong> {t('accuracyText')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('userInterfaceTitle')}</strong> {t('userInterfaceText')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('dataInputTitle')}</strong> {t('dataInputText')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('supportedOperationsTitle')}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('basicArithmeticTitle')}</strong> {t('basicArithmeticText')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('advancedOperationsTitle')}</strong> {t('advancedOperationsText')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('numberTheoryTitle')}</strong> {t('numberTheoryText')}</li>
        </ul>

        <h3 style={h3Style}>{t('inputFormatsTitle')}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('integerFormatTitle')}</strong> {t('integerFormatText')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('decimalFormatTitle')}</strong> {t('decimalFormatText')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('scientificNotationTitle')}</strong> {t('scientificNotationText')}</li>
        </ul>

        <h3 style={h3Style}>{t('precisionControlTitle')}</h3>
        <p style={paragraphStyle}>{t('precisionControlText')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={h3Style}>{t('faq1Question')}</h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={h3Style}>{t('faq2Question')}</h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={h3Style}>{t('faq3Question')}</h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={h3Style}>{t('faq4Question')}</h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={h3Style}>{t('faq5Question')}</h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.yeschat.ai/gpts-2OToA7zOhc-AI-Big-Numbers-Calculator" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('ref1')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="http://www.mrob.com/pub/perl/hypercalc.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('ref2')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.boxentriq.com/code-breaking/big-number-calculator" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('ref3')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.calculator.net/big-number-calculator.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('ref4')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://defuse.ca/big-number-calculator.htm" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('ref5')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}